<template>
  <!-- Mobile menu, show/hide based on menu open state. -->
  <div
    v-if="isOpen"
    class="menu-backdrop"
  />

  <Transition name="slide">
    <nav
      v-if="isOpen"
      class="nav-mobile"
      :class="isOpen ? 'showMobileNav' : ''"
    >
      <aside class="wrapper">
        <section class="header">
          <AppLink
            :to="{name: 'app_event_list'}"
            @click="close()"
          >
            <span class="sr-only">SportPix</span>
            <img
              loading="lazy"
              class="logo"
              src="@/assets/images/global/app-nav/sportpix-logo.svg"
              alt="SportPix Logo"
            >
          </AppLink>

          <AppButton
            screen-reader-text="Close menu"
            @click="close()"
          >
            <FontAwesomeIcon
              :icon="faCircleXmark"
              size="xl"
              fixed-width
            />
          </AppButton>
        </section>

        <section class="body">
          <menu>
            <li
              v-for="(link, index) in menuLinks"
              :key="index"
              @click="close()"
            >
              <AppLink :to="link.to">
                {{ link.name }}
              </AppLink>
            </li>
          </menu>

          <template v-if="!user">
            <menu>
              <li>
                <AppButton @click="[$emit('login'), close()]">
                  Log In
                </AppButton>
              </li>

              <li>
                <AppButton @click="[$emit('register'), close()]">
                  Register
                </AppButton>
              </li>
            </menu>
          </template>

          <template v-else>
            <menu>
              <li
                v-if="user"
                class="account-item"
              >
                {{ user }}
              </li>

              <li>
                <AppLink
                  :to="{ name: 'app_profile' }"
                  @click="close()"
                >
                  Profile
                </AppLink>
              </li>

              <li>
                <AppButton
                  class="text-sm lg:text-base"
                  @click="[$emit('logout'), close()]"
                >
                  Log out
                </AppButton>
              </li>
            </menu>
          </template>
        </section>

        <section class="footer">
          <menu class="socials">
            <li>
              <a
                :href="`mailto:${ContactInfo.InfoEmailAddress}`"
                target="_blank"
                @click="close()"
              >
                <FontAwesomeIcon
                  fixed-width
                  :icon="faEnvelope"
                />
                <span class="sr-only">support@sportpix.co.za</span>
              </a>
            </li>

            <li>
              <AppLink
                :to="ContactInfo.InstagramHandle"
                @click="close()"
              >
                <FontAwesomeIcon
                  fixed-width
                  :icon="faSquareInstagram"
                />
                <span class="sr-only">Instagram</span>
              </AppLink>
            </li>

            <li>
              <AppLink
                :to="ContactInfo.FacebookPage"
                @click="close()"
              >
                <FontAwesomeIcon
                  fixed-width
                  :icon="faFacebookSquare"
                />
                <span class="sr-only">Facebook</span>
              </AppLink>
            </li>
          </menu>

          <menu class="secondary-links">
            <li>
              <AppLink
                :to="{name: 'app_privacy_policy'}"
                @click="close()"
              >
                Privacy Policy
              </AppLink>
            </li>
            <li>
              <AppLink
                :to="{name: 'app_terms_of_use'}"
                @click="close()"
              >
                Terms of Use
              </AppLink>
            </li>
            <li>
              <AppLink
                :to="{name: 'app_faqs'}"
                @click="close()"
              >
                FAQs
              </AppLink>
            </li>
          </menu>

          <span class="copyright">Copyright © {{ currentYear }} SportPix</span>
        </section>
      </aside>
    </nav>
  </Transition>
</template>

<script setup>
import { onMounted } from 'vue';

import { AppButton, AppLink } from '@/components/app';

import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { faFacebookSquare, faSquareInstagram } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { ContactInfo } from '@/const';
import { useDisableBodyScroll } from '@/composables';

const currentYear = new Date().getFullYear();
const isOpen = defineModel();

defineProps({
  user: {
    type: String,
    default: null,
  },
  cartItems: {
    type: Array,
    default: () => [],
  },
});

defineEmits(['login', 'logout', 'register']);

const menuLinks = [
  { name: 'Find your Photos', to: { name: 'app_event_list' } },
  { name: 'About SportPix', to: { name: 'app_about_us' } },
];

onMounted(() => {
  useDisableBodyScroll(isOpen);
});

const close = () => {
  isOpen.value = false;
};

</script>
